import { ToastrService } from 'ngx-toastr';
import { HttpResponse } from '@angular/common/http';

export interface ApiResultStatus {
  success: boolean;
  statusCode: number;
  message: string;
}

export class ErrorHandler {
  constructor(private toastr: ToastrService) {}

  private static readonly unauthorizedMessage = 'You are not authorized to perform this operation';

  private static isSuccessStatusCode(statusCode: number): boolean {
    return typeof statusCode === 'number' && statusCode >= 200 && statusCode < 300;
  }

  private static normalizeStatusCode(rawStatusCode: any): number {
    if (typeof rawStatusCode === 'number' && Number.isFinite(rawStatusCode)) {
      return rawStatusCode;
    }

    if (typeof rawStatusCode === 'string') {
      const trimmedStatusCode = rawStatusCode.trim();

      if (/^40[eE]$/.test(trimmedStatusCode)) {
        return 403;
      }

      const numericStatusCode = Number(trimmedStatusCode);
      if (Number.isFinite(numericStatusCode)) {
        return numericStatusCode;
      }
    }

    return 0;
  }

  private static isUnauthorizedStatusCode(statusCode: number): boolean {
    return statusCode === 401 || statusCode === 403;
  }

  /**
   * Extracts status code from API response.
   * Checks: statusCode, status, code — on root and nested error object.
   */
  private static extractStatusCode(result: any): number {
    if (!result) return 0;
    return this.normalizeStatusCode(
      result.statusCode ??
      result.status ??
      result.code ??
      (result.error && (result.error.statusCode ?? result.error.status ?? result.error.code)) ??
      (result.response && (result.response.statusCode ?? result.response.status ?? result.response.code)) ??
      (result.body && (result.body.statusCode ?? result.body.status ?? result.body.code)) ??
      0
    );
  }

  /**
   * Extracts message from API response.
   * Checks: statusMessage, message — on root, error, and response objects.
   */
  private static extractMessage(result: any): string {
    if (!result) return 'An unknown error occurred.';
    return (
      result.statusMessage ||
      result.message ||
      (result.error && (result.error.statusMessage || result.error.message)) ||
      (result.response && (result.response.statusMessage || result.response.message)) ||
      'No message returned from API.'
    );
  }

  /**
   * Parses any API response into a structured result.
   * Handles statusCode, status, and code fields.
   * Use in services to get status without showing toastr.
   */
  static parseApiResult(response: any): ApiResultStatus {
    if (!response) {
      return { success: false, statusCode: 0, message: 'No response received from API.' };
    }
    const statusCode = this.extractStatusCode(response);
    const message = this.isUnauthorizedStatusCode(statusCode)
      ? this.unauthorizedMessage
      : this.extractMessage(response);
    const success = this.isSuccessStatusCode(statusCode);
    return { success, statusCode, message };
  }

  static showToastByStatusCode(
    statusCode: number,
    toastr: ToastrService,
    successMessage: string,
    errorMessage?: string,
    errorOptions?: { timeOut?: number; enableHtml?: boolean }
  ): boolean {
    const success = this.isSuccessStatusCode(statusCode);
    if (success) {
      toastr.success(successMessage, 'Success');
    } else {
      toastr.error(errorMessage || successMessage, 'Error', errorOptions);
    }
    return success;
  }

  static showStatusMessageToastr(response: any, toastr: ToastrService): ApiResultStatus {
    const result = this.parseApiResult(response);
    if (result.success) {
      toastr.success(result.message, 'Success');
    } else {
      toastr.error(result.message, 'Error');
    }
    return result;
  }

  /**
   * Parses an HttpResponse to extract HTTP status code from the response header
   * and message from the response body.
   * 
   * USE THIS instead of parseApiResult when using { observe: 'response' } in HTTP calls.
   * This extracts the REAL HTTP status code from the header (e.g. 200, 400, 500),
   * not from the API response body.
   *
   * FALLBACK: If httpStatusCode is not available, falls back to body-based extraction.
   * TODO: Once all endpoints return proper HTTP status codes, remove the fallback logic.
   */
  static parseHttpResponse(httpResponse: HttpResponse<any>): ApiResultStatus {
    const body = httpResponse?.body;
    const bodyParsed = this.parseApiResult(body);
    const httpStatusCode = httpResponse?.status ?? 0;

    // Prefer business status from body when present (many APIs return HTTP 200 with body statusCode 4xx/5xx).
    if (bodyParsed.statusCode > 0) {
      return bodyParsed;
    }

    // Extract message from response body (or wrapper) with HTTP status fallback.
    const message = this.extractMessage(body || httpResponse);

    // If HTTP status code is available and valid, use it directly.
    if (httpStatusCode > 0) {
      const success = this.isSuccessStatusCode(httpStatusCode);
      return {
        success,
        statusCode: httpStatusCode,
        message: this.isUnauthorizedStatusCode(httpStatusCode)
          ? this.unauthorizedMessage
          : message,
      };
    }

    return bodyParsed;
  }

  /**
   * Shows toastr with correct background color based on status code.
   * 2xx → green (success), 4xx/5xx → red (error), other → yellow (warning).
   * Returns the parsed result so caller can use it further.
   */
  static showToastrByStatus(response: any, context: string, toastr: ToastrService): ApiResultStatus {
    const result = this.parseApiResult(response);
    const displayMessage = `${context}: ${result.message}`;

    if (result.success) {
      toastr.success(displayMessage, 'Success');
    } else if (result.statusCode >= 400 && result.statusCode < 600) {
      toastr.error(displayMessage, 'Error');
    } else {
      toastr.warning(displayMessage, 'Warning');
    }

    return result;
  }

  static showCrudToasterByStatus(
    response: any,
    toastr: ToastrService,
    successMessage: string,
    errorMessage?: string,
    errorOptions?: { timeOut?: number; enableHtml?: boolean }
  ): ApiResultStatus {
    return this.showStatusMessageToastr(response, toastr);
  }

  static handleError(error: any, context: string): void {
    let errorMessage = '';
    if (error.error instanceof ErrorEvent) {
      errorMessage = `Client-side error in ${context}: ${error.error.message}`;
    } else {
      errorMessage = `Server-side error in ${context}: ${error.status} - ${error.message}`;
    }
    console.error(errorMessage);
  }

  static extractApiErrorMessage(error: any): string {
    if (!error) return 'An unknown error occurred.';
    return this.parseApiResult(error).message;
  }

  handleValidationError(field: string): void {
    const errorMessage = `Validation error: ${field} is required or invalid.`;
    this.toastr.warning(errorMessage, 'Validation Error');
  }

  handleGenericError(message: string): void {
    this.toastr.error(message, 'Error');
  }

  static handleApiError(error: any, context: string, toastr: ToastrService): void {
    const errorMessage = this.extractApiErrorMessage(error);
    toastr.error(`Error in ${context}: ${errorMessage}`, 'API Error');
    console.error(`Error in ${context}:`, error);
  }

  static handleApiResult(result: any, context: string, toastr: ToastrService): void {
    if (!result) {
      toastr.error(`No response received for ${context}`, 'API Response');
      console.error(`No response received for ${context}`);
      return;
    }

    const parsed = this.parseApiResult(result);
    const code = parsed.statusCode;
    const messageToShow = parsed.message;

    if (typeof code === 'number' && code >= 200 && code < 300) {
      toastr.success(messageToShow, 'Success');
    } else if (code >= 400 && code < 600) {
      toastr.error(messageToShow, 'API Response');
    } else {
      toastr.warning(messageToShow, 'API Response');
    }
  }

  static handleApiResponse(response: any, context: string, toastr: ToastrService): void {
    if (!response) {
      toastr.error(`No response received for ${context}`, 'API Response');
      console.error(`No response received for ${context}`);
      return;
    }
    const parsed = this.parseApiResult(response);
    const code = parsed.statusCode;
    const msg = parsed.message;

    if (code >= 200 && code < 300) {
      toastr.success(msg, 'Success');
    } else if (code >= 400 && code < 600) {
      toastr.error(msg, 'API Error');
    } else {
      toastr.warning(msg, 'API Warning');
    }
  }
}